'use client';

import React from 'react';
import { ErrorState } from '@/components/error/ErrorState';
import { getStateCopy } from '@/lib/copy/user-facing-messages';
import {
  captureClientError,
  openUserFeedbackDialog,
  type ErrorCorrelation,
} from '@/lib/observability/client-error-reporting';
import { logDashboard } from '@/lib/utils/logger';

interface Props {
  children: React.ReactNode;
  fallbackType?: 'auth' | 'profile' | 'component';
  componentName?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
  isRetrying: boolean;
  correlation: ErrorCorrelation | null;
}

const MAX_RETRIES = 3;
const RETRY_DELAYS_MS = [1000, 2000, 4000];

export class ClerkErrorBoundary extends React.Component<Props, State> {
  private retryTimeout: NodeJS.Timeout | null = null;
  private retryCount = 0;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      isRetrying: false,
      correlation: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      isRetrying: false,
      correlation: null,
    };
  }

  componentWillUnmount() {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
  }

  private isTransientError(error: Error): boolean {
    const message = error.message?.toLowerCase() || '';

    return (
      message.includes('network') ||
      message.includes('fetch') ||
      message.includes('timeout') ||
      message.includes('connection') ||
      message.includes('econnrefused') ||
      message.includes('enotfound') ||
      message.includes('rate limit') ||
      message.includes('429') ||
      message.includes('clerk_js_') ||
      message.includes('temporarily unavailable') ||
      message.includes('service unavailable')
    );
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const { componentName, fallbackType } = this.props;
    const isTransient = this.isTransientError(error);

    logDashboard.error('Clerk Error Boundary caught error', error, {
      componentName: componentName || 'Unknown Clerk Component',
      fallbackType: fallbackType || 'unknown',
      isTransient,
      retryCount: this.retryCount,
      componentStack: errorInfo.componentStack,
    });

    this.setState({
      correlation: captureClientError(error, {
        surface: 'clerk-error-boundary',
        componentStack: errorInfo.componentStack ?? undefined,
      }),
    });

    if (isTransient && this.retryCount < MAX_RETRIES) {
      this.scheduleRetry();
    }
  }

  private scheduleRetry() {
    const delayIndex = Math.min(this.retryCount, RETRY_DELAYS_MS.length - 1);
    const delayMs = RETRY_DELAYS_MS[delayIndex];

    logDashboard.info('Clerk Error Boundary scheduled retry', {
      componentName: this.props.componentName || 'Unknown Clerk Component',
      fallbackType: this.props.fallbackType || 'unknown',
      retryCount: this.retryCount + 1,
      delayMs,
    });

    this.setState({ isRetrying: true });

    this.retryTimeout = setTimeout(() => {
      this.retryCount += 1;
      this.setState({
        hasError: false,
        error: undefined,
        isRetrying: false,
        correlation: null,
      });
    }, delayMs);
  }

  private handleRetry = () => {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }

    this.retryCount += 1;
    this.setState({
      hasError: false,
      error: undefined,
      isRetrying: false,
      correlation: null,
    });
  };

  private getRenderableError(error: Error): unknown {
    if (this.props.fallbackType === 'auth') {
      const message = error.message?.toLowerCase() || '';

      if (message.includes('rate limit') || message.includes('429')) {
        return {
          message: error.message,
          code: 'RATE_LIMIT',
          status: 429,
          digest: (error as Error & { digest?: string }).digest,
        };
      }

      if (
        message.includes('network') ||
        message.includes('fetch') ||
        message.includes('timeout') ||
        message.includes('connection') ||
        message.includes('econnrefused') ||
        message.includes('enotfound')
      ) {
        return {
          message: 'Network timeout',
          code: 'NETWORK_ERROR',
          digest: (error as Error & { digest?: string }).digest,
        };
      }

      if (
        message.includes('clerk_js_') ||
        message.includes('temporarily unavailable') ||
        message.includes('service unavailable')
      ) {
        return {
          message: error.message,
          code: 'SERVICE_UNAVAILABLE',
          status: 503,
          digest: (error as Error & { digest?: string }).digest,
        };
      }

      return {
        message: error.message,
        code: 'AUTH_UNAVAILABLE',
        status: 503,
        digest: (error as Error & { digest?: string }).digest,
      };
    }

    if (!this.isTransientError(error)) {
      return error;
    }

    return {
      message: error.message,
      code: 'SERVICE_UNAVAILABLE',
      status: 503,
      digest: (error as Error & { digest?: string }).digest,
    };
  }

  private getFallbackConfig() {
    switch (this.props.fallbackType) {
      case 'auth':
        return {
          homeHref: '/',
          homeLabel: 'Go Home',
          sectionLabel: this.props.componentName || 'sign in',
        };
      case 'profile':
        return {
          homeHref: '/dashboard',
          homeLabel: 'Open Dashboard',
          sectionLabel: this.props.componentName || 'settings',
        };
      case 'component':
      default:
        return {
          homeHref: '/dashboard',
          homeLabel: 'Open Dashboard',
          sectionLabel: this.props.componentName || 'this panel',
        };
    }
  }

  render() {
    if (this.state.hasError && this.state.error) {
      const pageErrorCopy = getStateCopy('pageError');
      const fallbackConfig = this.getFallbackConfig();

      return (
        <>
          <ErrorState
            error={this.getRenderableError(this.state.error)}
            eventId={this.state.correlation?.eventId ?? undefined}
            homeHref={fallbackConfig.homeHref}
            homeLabel={fallbackConfig.homeLabel}
            onRetry={this.handleRetry}
            onReport={
              this.state.correlation?.eventId
                ? () => {
                    openUserFeedbackDialog(this.state.correlation);
                  }
                : undefined
            }
            reportAction={pageErrorCopy.reportAction}
            sectionLabel={
              this.props.fallbackType === 'auth'
                ? undefined
                : fallbackConfig.sectionLabel
            }
            supportHref="/support"
            viewport={this.props.fallbackType === 'auth' ? 'screen' : 'inline'}
          />
          {this.props.fallbackType === 'auth' && this.state.isRetrying ? (
            <p className="sr-only" role="status">
              {getStateCopy('authUnavailable').retrying}
            </p>
          ) : null}
        </>
      );
    }

    return this.props.children;
  }
}
